/**
 * A counting semaphore for fan-out that would otherwise all leave at once.
 *
 * Requests to one host contend: measured cold, thirteen market sparklines
 * fired in a single wave and each took ~880ms, where a handful at a time
 * return in a fraction of that. Queueing does not make the page slower — the
 * same work is done, just not all at the same instant — and it stops a
 * background fan-out from crowding out the requests a user is waiting on.
 */
export function limitConcurrency(max: number) {
    let active = 0;
    const waiting: (() => void)[] = [];

    const release = (): void => {
        const resume = waiting.shift();
        // Hand the slot straight to the next waiter; `active` is unchanged
        // because the slot never actually goes idle.
        if (resume) resume();
        else active--;
    };

    return async function run<T>(task: () => Promise<T>): Promise<T> {
        if (active < max) active++;
        else await new Promise<void>((resolve) => waiting.push(resolve));
        try {
            return await task();
        } finally {
            release();
        }
    };
}
